import React, { useState, useEffect } from 'react'
import cn from 'classnames'
import generateUuid from 'uuid/v4'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { isEmpty, map, reduce } from 'lodash'

import BackButtonChevron from '../../../components/BackButtonChevron'
import GratuityHeader from '../../../components/GratuityHeader'
import Button from '../../../components/Button'
import { ReactComponent as RadioSelected } from '../../../assets/icons/radio-selected.svg'
import { ReactComponent as RadioUnSelected } from '../../../assets/icons/radio-unselected.svg'

import { addPaymentToOrderInProgress, closeNoOpTender } from '../../../actions/order'
import { centsToDollars } from '../../../utils/formatters'
import { getCashEnabled, getTicketEnabled, makeGetNoOpTenders } from '../../../selectors/revenueCenter'

import styles from './TenderSelect.module.scss'
import { getTipsEnabled } from '../../../selectors/menus'
import { SIGNATURE_THRESHOLD_AMOUNT, USE_RICH_CHECKOUT } from '../../../constants'
import { getOrderInProgress } from '../../../selectors/order'
import { getTotal } from '../../../selectors/cart'
import ConfirmSplitPaymentDialog from '../../../components/dialog/ConfirmSplitPaymentDialog'
import { getSafMode } from '../../../selectors/appState'
import useRemoteOrderTotal from '../../../hooks/useRemoteOrderTotal'
import ConnectingIndicator from '../../../components/ConnectingIndicator'


const TenderSelect = ({
  addPaymentToOrder,
  className,
  closeWithCash,
  goBack,
  paymentTypes,
  push,
  offlineTotal,
  fromTicketTender,
  order,
  isNewOrder,
  closeNoOpTender,
  tipsEnabled,
  orderAmountInCents,
  tenderAmountInCents,
}) => {
  const classNames = cn(styles.tenderSelect, className)
  const [showSplitPayDialog ,setShowSplitPayDialog] = useState(false)
  const [paymentId] = useState(isNewOrder ? '' : generateUuid())
  const [paymentType, setPaymentType] = useState('')
  const [tenderDisplayName, setTenderDisplayName] = useState('')
  const [loadRemoteOrderTotal, { data: remoteOrderTotal, loading, success, failed }] = useRemoteOrderTotal()
  const total = isNewOrder ? remoteOrderTotal?.totalAmountInCents ?? offlineTotal : tenderAmountInCents
  const tenderDisplay = centsToDollars(total)
  const threshHoldExceeded = (SIGNATURE_THRESHOLD_AMOUNT || SIGNATURE_THRESHOLD_AMOUNT === 0) && total > SIGNATURE_THRESHOLD_AMOUNT
  const signatureRequired = tipsEnabled || threshHoldExceeded

  const mediaQueryTablet = window.matchMedia('(max-width: 1920px)')
  const mediaQueryMobile = window.matchMedia('(max-width: 360px)')
  let modal = null
  
  useEffect(() => {
    loadRemoteOrderTotal()
  }, [])

  const handleNext = () => {
    if (paymentType === "cash") {
      if (isNewOrder) {
        push("/change-calculator");
      } else {
        closeWithCash(paymentId, total);
      }
    } else if (paymentType === "card") {
      if (signatureRequired) {
        if (isNewOrder) {
          push("/signature");
        } else {
          push({
            pathname: `/signature/${paymentId}`,
            state: {
              tenderAmountInCents: total,            },
          })
        }
      } else {
        push({
          pathname: `/card-reader`,
          state: {
            paymentId: !isNewOrder && paymentId,
            tenderAmountInCents: !isNewOrder && total,
          },
        });
      }
    } else if (paymentType === "loaded_ticket") {
      if (tipsEnabled && isNewOrder) {
        push({
          pathname: '/signature',
          state: {
            addTipThenRedirectPath: '/ticket'
          }
        })
      } else {
        push("/ticket")
      }
    } else if (paymentType === "mobile_wallet") {
      push("/rich-checkout");
    } else if (paymentType === "split") {
      setShowSplitPayDialog(true)
    } else {
      if (isNewOrder) {
        if (tipsEnabled) {
          push({
            pathname: `/signature`,
            state: {
              customTender: { name: paymentType, displayName: tenderDisplayName }
            },
          })
        } else {
          closeNoOpTender({ name: paymentType, displayName: tenderDisplayName })
        }
      } else {
        addPaymentToOrder(total, paymentId, paymentType, tenderDisplayName)
      }
    }
  };

  if (!success && !failed) {
    return <ConnectingIndicator />
  }

  const makeTenderSelection = ([payment, display]) => {
    const selected = paymentType === payment
    return <div className={cn([styles.tenderSelection, selected && styles.selected])} onClick={() => {setPaymentType(payment); setTenderDisplayName(display)}}>
    {selected ? <RadioSelected className={styles.icon}/> : <RadioUnSelected className={styles.icon}/>}
    {display}
    </div>
  }

  const tenderSelections = map(paymentTypes, makeTenderSelection)

  if (showSplitPayDialog) {
    modal = (
      <ConfirmSplitPaymentDialog
        onClose={() => setShowSplitPayDialog(false)}
        fromConcessions={false}
      />
    )
  }
  
  
  const primaryText = centsToDollars(total)
  const secondaryText = fromTicketTender ? 'Please select a tender to pay balance due' : 'Please Select Tender'
  const extraText = `Total: ${centsToDollars(orderAmountInCents)} • Balance Paid: ${centsToDollars(orderAmountInCents - order?.balanceDueInCents)}` 

  return (
    <div className={classNames}>
      <div className={styles.goBack}>
        <BackButtonChevron
          onClick={goBack}
          backButtonText=''
        />
        <span className={styles.mobileTitle}>{tenderDisplay}</span>
        <span></span>
      </div>
      <div className={styles.tenderSelect__header}>
        <GratuityHeader
          className={styles.gratuityHeader}
          primaryText={primaryText}
          secondaryText={secondaryText}
          extraText={isNewOrder ? '' : extraText}
          isSplitTender={true}
          isMobileView={mediaQueryTablet?.matches || mediaQueryMobile?.matches}
        />
      </div>
      <div className={styles.tenderSelect__body}>
        <div className={styles.tenderSelections}>
         {tenderSelections}
        </div>
        <div className={styles.actionButtons}>
          <Button className={cn(styles.button, styles.confirm)} disabled={!paymentType} onClick={handleNext}>
            Next
          </Button>
        </div>
      {modal}
      </div>
    </div>
  )
}

const mapStateToProps = (state, props) => {
  const push = props?.history?.push ?? (() => {})
  const tenderAmountInCents = props?.history?.location?.state?.tenderAmountInCents ?? 0
  const offlineTotal = getTotal(state)
  const order = getOrderInProgress(state)
  const isNewOrder = isEmpty(order)
  const fromTicketTender = props?.history?.location?.state?.fromTicketTender ?? false
  const noOpTenders = makeGetNoOpTenders()(state)
  const cashEnabled = getCashEnabled(state)
  const tipsEnabled = getTipsEnabled(state)
  const orderAmountInCents = order?.amountInCents
  const safMode = getSafMode(state)
  const networkAvailable = (state?.config?.networkAvailable ?? false) && safMode !== 2
  const richCheckoutAvailable = USE_RICH_CHECKOUT === "true"
  const ticketEnabled = getTicketEnabled(state) && networkAvailable

  let paymentTypes = []

  if (isNewOrder) {
    if (richCheckoutAvailable) {
      paymentTypes.push(['mobile_wallet', 'App'])
    }
    if (networkAvailable) {
      paymentTypes.push(['split', 'Split'])
    }
  }

  if (ticketEnabled && (isNewOrder || fromTicketTender)) {
    paymentTypes.push(['loaded_ticket', 'Ticket'])
  }

  paymentTypes.push(['card', 'Card'])

  if (cashEnabled) {
    paymentTypes.push(['cash', 'Cash'])
  }

  paymentTypes = reduce(noOpTenders, (payment, tender) => {
    return [
      ...payment,
      [tender.name, tender.displayName],
    ]
  }, paymentTypes)

  return {
    tenderAmountInCents,
    paymentTypes,
    push,
    cashEnabled,
    fromTicketTender,
    order,
    isNewOrder,
    offlineTotal,
    tipsEnabled,
    orderAmountInCents,
  }
}

const mapDispatchToProps = (dispatch, props) => {
  const goBack = props?.history?.goBack ?? (() => {})
  const push = props?.history?.push ?? (() => {})

  return {
    goBack: () => {
      goBack()
    },
    closeNoOpTender: (tender) => dispatch(closeNoOpTender({ tender })),
    closeWithCash: (paymentId, tenderAmountInCents) => {
      push({
        pathname: `/change-calculator/${paymentId}`,
        state: {
          tenderAmountInCents,
        }
      })
    },
addPaymentToOrder: (tenderAmountInCents, paymentId, paymentType, tenderDisplayName) =>
  dispatch(addPaymentToOrderInProgress({ tenderAmountInCents, paymentId, paymentType, tenderDisplayName }))
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TenderSelect))
